import { useEffect } from 'react';

import { logger } from '../../datadog/logger';
import { useMuteAudio, useStopVideo, useSwitchCamera, useSwitchMicrophone } from '../../video-sdk';
import { useValidateStoredDeviceIdsCallback } from './useValidateStoredDeviceIdsCallback';

/**
 * When a device change event is detected, this hook will update the list of
 * available devices and switch to the first available devices if the current
 * devices are no longer available.
 */
export function useHandleDeviceChanges() {
  const changeCamera = useSwitchCamera();
  const changeMicrophone = useSwitchMicrophone();
  const validateStoredDeviceIds = useValidateStoredDeviceIdsCallback();
  const stopVideo = useStopVideo();
  const stopAudio = useMuteAudio();

  const releaseCamera = () => {
    return stopVideo(true);
  };
  const releaseMicrophone = () => {
    return stopAudio(true);
  };

  const handleDeviceDisconnects = async () => {
    const { status, value: validationResponse } = await validateStoredDeviceIds();
    if (status !== 'failure') return;
    const { invalidMediaKinds, availableDevices } = validationResponse;
    for (const kind of invalidMediaKinds) {
      switch (kind) {
        case 'audio': {
          const [firstAvailableDevice] = availableDevices.audioInputDevices;
          const firstAvailableDeviceId = firstAvailableDevice?.deviceId;
          if (!firstAvailableDeviceId) {
            logger.info('Microphone disconnected. No other devices found. Releasing track.');
            await releaseMicrophone();
            break;
          }
          logger.info('Microphone disconnected. Changing to different device.', {
            deviceId: firstAvailableDeviceId,
            label: firstAvailableDevice.label,
          });
          await changeMicrophone(firstAvailableDeviceId);
          break;
        }
        case 'video': {
          const [firstAvailableDevice] = availableDevices.videoInputDevices;
          const firstAvailableDeviceId = firstAvailableDevice?.deviceId;
          if (!firstAvailableDeviceId) {
            logger.info('Camera disconnected. No other devices found. Releasing track.');
            await releaseCamera();
            break;
          }
          logger.info('Camera disconnected. Changing to different device.', {
            deviceId: firstAvailableDeviceId,
            label: firstAvailableDevice.label,
          });
          await changeCamera(firstAvailableDeviceId);
          break;
        }
      }
    }
  };

  return useEffect(
    function handleDeviceChangeListener() {
      const abortController = new AbortController();
      navigator.mediaDevices.addEventListener('devicechange', handleDeviceDisconnects, {
        signal: abortController.signal,
      });
      return () => abortController.abort();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
